import btoa from 'btoa';

export const customToString = (string: string | any) => {
    return string ? string.toString() : '';
};

/**
 * Get API Error Message
 * @param response
 */
export const getApiErrorMessage = (response: any) => {
    const responseMsg = response?.data?.error ? response?.data?.error : null;
    const unhandleErrorMsg = response?.message ? response?.message : null;
    const unhandleErrorMsg2 = response?.data?.errors?.error ? response?.data?.errors?.error : null;
    const error = responseMsg || unhandleErrorMsg || unhandleErrorMsg2 || 'Something went wrong. please try again';
    if (typeof error === 'string') {
        return error;
    }
    return error.length ? error[0] : error;
};

/**
 * Get Query Params by URL
 * @param base
 * @param params
 */
export const getURLWithQueryParams = (base: string, params: any) => {
    const query = Object.keys(params)
        .map((key) => `${key}=${params[key]}`)
        .join('&');

    return `${base}?${query}`;
};

/**
 * Is Number Only
 * @param event
 */
export const isNumberOnly = (event: any) => {
    const inputValue = event.charCode;
    if (!(inputValue >= 48 && inputValue <= 57)) {
        event.preventDefault();
    }
};

/**
 * Get Extension
 * @param file
 */

export const getExtension = (file: any) => {
    const mimeType = file.type;
    if (mimeType) {
        return mimeType.split('/').pop();
    }
};

/**
 * Application File Types Allowed
 * @param mimeType
 */
export const fileTypeAllowed = (mimeType: string) => {
    const SUPPORTED_FORMATS = ['video/mp4', 'video/mov', 'video/webm', 'image/png', 'image/jpg', 'image/jpeg'];
    return SUPPORTED_FORMATS.includes(mimeType);
};

/**
 * Remove Object
 * @param payloadData
 */
export const removeFileObject = (payloadData: any) => {
    const { fileProgress, id } = payloadData;

    return Object.keys(fileProgress)
        .filter((key) => {
            return parseInt(key) !== id;
        })
        .reduce((obj: any, key: any) => {
            // eslint-disable-next-line no-param-reassign
            obj[key] = fileProgress[key];
            return obj;
        }, {});
};

/**
 * Get Round Number
 * @param data
 */
export const toFixedPrice = (data: any) => {
    if (!Number.isInteger(data)) {
        return data.toFixed(2);
    }
    return data;
};

/**
 * Simple deep copy with JSON.parse and JSON.stringify except the method copy of the source object to target object
 *
 * @param value T
 * @returns T
 */
export const simpleDeepCopy = <T>(value: T): T => {
    try {
        return JSON.parse(JSON.stringify(value));
    } catch (e) {
        return value;
    }
};

/**
 * Camel Case to Normal Word
 * @param string
 */
export const camelCaseToWord = (string: string) => {
    if (string) {
        const first = string.replace(/_/g, ' ');
        return first.replace(/^[a-z]|[A-Z]/g, (c, i) => (i ? ' ' : '') + c.toUpperCase());
    }
    return string;
};

/**
 * Capital Letter
 * @param string
 */
export const capitalLetter = (string: string) => {
    if (string) {
        return string.toUpperCase();
    }
    return string;
};

/**
 * First Letter Caps
 * @param string
 */
export const firstLetterCaps = (string: string | null) => {
    if (string) {
        const convertString = string.charAt(0).toUpperCase() + string.slice(1);
        return convertString.trim();
    }
    return '';
};

/**
 * Number Format Converter
 * @param num
 */
export const numFormatter = (num: number | null) => {
    if (num) {
        if (num > 999 && num < 1000000) {
            return `${(num / 1000).toFixed(0)}K`; // convert to K for number from > 1000 < 1 million
        }
        if (num > 1000000) {
            return `${(num / 1000000).toFixed(0)}M`; // convert to M for number from > 1 million
        }
        if (num < 900) {
            return num; // if value < 1000, nothing to do
        }
    }
    return num;
};

/**
 * Encrypt Data
 * @param str string | any
 * @returns string
 */
export const simpleEncrypt = (str: string | any): string => {
    if (!str) {
        return '';
    }
    return btoa(str);
};

/**
 * Decrypt Data
 * @param encryptStr string | null | string[]
 * @returns string
 */
export const simpleDecrypt = (encryptStr: string | null | string[] | any): string => {
    return typeof encryptStr === 'string' && isBase64Str(encryptStr) ? atob(encryptStr) : '';
};

/**
 * Validate string is Base64 encoded
 * @param value string
 * @returns boolean
 */
export const isBase64Str = (value: string): boolean => {
    const base64regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;
    return base64regex.test(value);
};

/**
 * String To JSON Converter
 * @param value
 * @returns
 */
export const stringToJSON = (value: string) => {
    const splitData = value.split(';');
    const json: any = [];
    splitData.forEach((data) => {
        const dataSplit = data.trim().split('=');
        const key = dataSplit[0];
        const jsonData = {
            [key]: dataSplit[1],
        };
        json.push(jsonData);
    });

    return Object.assign({}, ...json);
};

/**
 * Limit String Viewed
 * @param value
 * @param limit
 */
export const limitedString = (value: string, limit: number) => {
    if (value.length > limit) {
        return `${value.substr(0, limit)}...`;
    }
    return value;
};

/**
 * Words In Plural
 * @param count
 * @param word
 * @returns
 */
export const wordsInPlural = (count: number | null, word: string) => {
    let str = word;
    if (count !== null && (count === 0 || count > 1)) {
        str += 's';
    }
    return str;
};

export const filterTwoData = (arrayData: any, ids: number[] | string[]) => {
    const results = arrayData.filter((data: any) => ids.some((sId) => data.id === sId));
    return results;
};

/**
 * Email is Valid
 * @param email
 * @returns
 */
export const emailIsValid = (email: string) => {
    return /\S+@\S+\.\S+/.test(email);
};

export const isValidPhone = (phoneNumber: string) => {
    const found = phoneNumber.search(
        /^(\+{1}\d{2,3}\s?[(]{1}\d{1,3}[)]{1}\s?\d+|\+\d{2,3}\s{1}\d+|\d+){1}[\s|-]?\d+([\s|-]?\d+){1,2}$/
    );
    if (found > -1) {
        return true;
    } else {
        return false;
    }
};
